import {
    FETCH_COMPARISON,
    FETCH_COMPARISON_FAIL,
    FETCH_COMPARISON_RUNS_GAVE_UP,
    FETCH_COMPARISON_RUNS_RETRY,
    FETCH_COMPARISON_RUNS_SUCCESS,
    FETCH_COMPARISON_SUCCESS,
    FETCH_WORKSPACE_CONFIG,
    FETCH_WORKSPACE_CONFIG_FAIL,
    FETCH_WORKSPACE_CONFIG_SUCCESS,
    FOLLOW_COMPARISON,
    START_COMPARISON,
    START_COMPARISON_FAIL,
    START_COMPARISON_SUCCESS
} from './actionTypes';
import {
    Connection,
    DEFAULT_COMPARISON_PLAYER_LIMIT
} from '../../simulation.model';

/* ---- The workspace that is being changed: its rules and its risk configuration ---- */

export const workspaceConfigInitialState = {
    workspace: null,
    rules: [],
    // The risk factors by category (AML, RG, FRAUD)
    risk: {},
    error: null,
    loading: false,
    loaded: false
};

export function workspaceConfigReducer(
    state = workspaceConfigInitialState,
    action
) {
    switch (action.type) {
        case FETCH_WORKSPACE_CONFIG:
            return {
                ...workspaceConfigInitialState,
                workspace: action.payload.workspace,
                loading: true
            };
        case FETCH_WORKSPACE_CONFIG_SUCCESS:
            // An answer for a workspace that is not the one asked for last is of no use any more
            return action.payload.workspace === state.workspace
                ? {
                      ...state,
                      rules: action.payload.rules,
                      risk: action.payload.risk,
                      loading: false,
                      loaded: true
                  }
                : state;
        case FETCH_WORKSPACE_CONFIG_FAIL:
            return { ...state, error: action.payload, loading: false };
        default:
            return state;
    }
}

/* ---- The comparison being started, followed and read ---- */

export const ComparisonPhase = Object.freeze({
    IDLE: 'IDLE',
    // Both runs are being followed
    FOLLOWING: 'FOLLOWING',
    // The runs completed and the comparison is being read
    LOADING: 'LOADING',
    DONE: 'DONE',
    // A run did not complete, or the comparison was refused
    FAILED: 'FAILED'
});

export const comparisonInitialState = {
    baselineRunId: null,
    variantRunId: null,
    baseline: null,
    variant: null,
    data: null,
    playerLimit: DEFAULT_COMPARISON_PLAYER_LIMIT,
    phase: ComparisonPhase.IDLE,
    // Whether what is shown is up to date, while the runs are being followed (like the connection of a run)
    connection: Connection.OK,
    error: null,
    submitting: false,
    // Why the last comparison that was asked for was refused, until another is asked for (see the runs: activeRunId)
    refusal: null
};

export function comparisonReducer(state = comparisonInitialState, action) {
    switch (action.type) {
        case START_COMPARISON:
        case START_COMPARISON_SUCCESS:
            return {
                ...state,
                submitting: action.type === START_COMPARISON,
                refusal: null
            };
        case START_COMPARISON_FAIL:
            return { ...state, submitting: false, refusal: action.payload };
        case FOLLOW_COMPARISON: {
            const { baselineRunId, variantRunId } = action.payload;
            const samePair =
                state.baselineRunId === baselineRunId &&
                state.variantRunId === variantRunId;

            return samePair
                ? {
                      ...state,
                      error: null,
                      connection: Connection.OK,
                      phase: ComparisonPhase.FOLLOWING
                  }
                : {
                      ...comparisonInitialState,
                      baselineRunId,
                      variantRunId,
                      phase: ComparisonPhase.FOLLOWING
                  };
        }
        case FETCH_COMPARISON_RUNS_SUCCESS:
            return {
                ...state,
                baseline: action.payload.baseline,
                variant: action.payload.variant,
                connection: Connection.OK
            };
        case FETCH_COMPARISON_RUNS_RETRY:
            return { ...state, connection: Connection.RECONNECTING };
        case FETCH_COMPARISON_RUNS_GAVE_UP:
            // What was last read stays, and the phase with it: it is the connection that failed, not the runs
            return { ...state, connection: Connection.LOST };
        case FETCH_COMPARISON:
            return {
                ...state,
                playerLimit: action.payload.playerLimit || state.playerLimit,
                phase: ComparisonPhase.LOADING
            };
        case FETCH_COMPARISON_SUCCESS:
            return {
                ...state,
                data: action.payload,
                error: null,
                phase: ComparisonPhase.DONE
            };
        case FETCH_COMPARISON_FAIL:
            return {
                ...state,
                error: action.payload,
                phase: ComparisonPhase.FAILED
            };
        default:
            return state;
    }
}
